'use client';

import { useCallback, useEffect, useState } from 'react';

import Image from 'next/image';
import { useTranslations } from 'next-intl';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Link } from '@/i18n/routing';
import {
  buildCookieConsent,
  DEFAULT_COOKIE_CONSENT,
  isCookieConsentCurrent,
  OPEN_COOKIE_SETTINGS_EVENT,
  openCookiePreferences,
  persistCookieConsent,
  readStoredCookieConsent,
  type CookieConsent,
} from '@/lib/analytics/cookie-consent';
import { cn } from '@/lib/utils';

export type { CookieConsent };

type BannerView = 'simple' | 'customize';

interface CookieSettingsButtonProps {
  className?: string;
}

export function CookieSettingsButton({ className }: CookieSettingsButtonProps) {
  const t = useTranslations('common.footer');

  return (
    <button
      type='button'
      onClick={() => {
        openCookiePreferences();
      }}
      className={className}
    >
      {t('cookieSettings')}
    </button>
  );
}

const primaryActionClass =
  'h-12 min-h-12 rounded-xl bg-orange-peel text-sm font-semibold text-white shadow-none hover:bg-orange-peel/90 focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2';

const secondaryActionClass =
  'h-12 min-h-12 rounded-xl border border-oxford-blue/20 bg-seasalt-50 text-sm font-semibold text-oxford-blue shadow-none hover:bg-seasalt-100 hover:text-oxford-blue';

const outlineActionClass =
  'h-12 min-h-12 rounded-xl border border-oxford-blue/20 bg-white text-sm font-semibold text-oxford-blue shadow-none hover:bg-seasalt-50';

const categorySwitchClass =
  'data-[state=checked]:border-oxford-blue data-[state=checked]:bg-oxford-blue disabled:opacity-100';

async function logConsentChoice(consent: CookieConsent): Promise<void> {
  try {
    let userId: string | null = null;
    try {
      const sessionResponse = await fetch('/api/auth/me', {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (sessionResponse.ok) {
        const session = (await sessionResponse.json()) as {
          user?: { id?: string | null } | null;
        };
        userId = session.user?.id || null;
      }
    } catch {
      // Anonymous visitors still have a valid local consent record.
    }

    await fetch('/api/consent/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        consent,
        userId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      }),
    });
  } catch (error) {
    console.error('Failed to log consent to server:', error);
  }
}

export function CookieBanner() {
  const t = useTranslations('common.cookieBanner');
  const [isVisible, setIsVisible] = useState(false);
  const [view, setView] = useState<BannerView>('simple');
  const [isDecisionRequired, setIsDecisionRequired] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_COOKIE_CONSENT);

  useEffect(() => {
    const handleOpenSettings = () => {
      const stored = readStoredCookieConsent();
      if (stored && isCookieConsentCurrent(stored)) {
        setConsent(stored);
        setIsDecisionRequired(false);
        setView('customize');
      } else {
        setConsent(DEFAULT_COOKIE_CONSENT);
        setIsDecisionRequired(true);
        setView('simple');
      }
      setIsVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);

    const stored = readStoredCookieConsent();
    if (stored && isCookieConsentCurrent(stored)) {
      setConsent(stored);
      setIsVisible(false);
      setIsDecisionRequired(false);
      return () => {
        window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
      };
    }

    setConsent(DEFAULT_COOKIE_CONSENT);
    setIsDecisionRequired(true);
    setView('simple');
    setIsVisible(true);

    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    };
  }, []);

  const saveConsent = useCallback(async (next: CookieConsent) => {
    const stored = persistCookieConsent(next);
    setConsent(stored);
    setIsVisible(false);
    setIsDecisionRequired(false);
    setView('simple');
    void logConsentChoice(stored);
  }, []);

  const handleAllowAll = useCallback(() => {
    void saveConsent(
      buildCookieConsent({
        preferences: true,
        performance: true,
        marketing: true,
        adsMeasurement: true,
        adsPersonalization: true,
      }),
    );
  }, [saveConsent]);

  const handleDeny = useCallback(() => {
    void saveConsent(
      buildCookieConsent({
        preferences: false,
        performance: false,
        marketing: false,
        adsMeasurement: false,
        adsPersonalization: false,
      }),
    );
  }, [saveConsent]);

  const handleAllowSelection = useCallback(() => {
    void saveConsent(
      buildCookieConsent({
        preferences: consent.preferences,
        performance: consent.performance,
        marketing: consent.marketing,
        adsMeasurement: consent.adsMeasurement,
        adsPersonalization: consent.adsPersonalization,
      }),
    );
  }, [consent, saveConsent]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setIsVisible(true);
        return;
      }
      if (isDecisionRequired) {
        return;
      }
      setIsVisible(false);
    },
    [isDecisionRequired],
  );

  return (
    <Dialog open={isVisible} onOpenChange={handleOpenChange}>
      <DialogContent
        size='md'
        overlayClassName='z-[220] bg-oxford-blue/40 sm:backdrop-blur-[2px]'
        className={cn(
          'z-[230] gap-0 overflow-hidden border-oxford-blue/10 bg-white p-0 shadow-e4 outline-none',
          // Mobile: bottom sheet. Desktop: centered card.
          'fixed inset-x-0 bottom-0 top-auto max-h-[min(92dvh,40rem)] w-full max-w-none translate-x-0 translate-y-0 rounded-t-2xl',
          'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl',
        )}
        onPointerDownOutside={event => {
          if (isDecisionRequired) {
            event.preventDefault();
          }
        }}
        onInteractOutside={event => {
          if (isDecisionRequired) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={event => {
          if (isDecisionRequired) {
            event.preventDefault();
          }
        }}
      >
        <DialogTitle className='sr-only'>{t('preferencesHeading')}</DialogTitle>
        <DialogDescription className='sr-only'>{t('shortBody')}</DialogDescription>

        <div className='flex max-h-[min(92dvh,40rem)] flex-col'>
          <header className='flex items-center justify-between gap-3 border-b border-oxford-blue/10 px-5 py-4 sm:px-6'>
            <div className='flex min-w-0 items-center gap-3'>
              <span className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-seasalt-50 ring-1 ring-oxford-blue/10'>
                <Image
                  src='/images/Logos/logo-1.svg'
                  alt=''
                  width={22}
                  height={22}
                  className='h-[22px] w-[22px] brightness-0'
                  unoptimized
                />
              </span>
              <div className='min-w-0 text-left'>
                <p className='truncate text-base font-semibold tracking-tight text-oxford-blue'>
                  {t('preferencesHeading')}
                </p>
                <p className='truncate text-xs text-oxford-blue/55'>Kleaners</p>
              </div>
            </div>
            {!isDecisionRequired ? (
              <button
                type='button'
                onClick={() => setIsVisible(false)}
                className='min-h-11 shrink-0 rounded-xl px-3 text-sm font-medium text-oxford-blue/65 hover:bg-seasalt-50 hover:text-oxford-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel'
              >
                {t('close')}
              </button>
            ) : null}
          </header>

          {view === 'simple' ? (
            <div className='flex flex-1 flex-col gap-6 px-5 py-5 sm:px-6 sm:py-6'>
              <p className='text-left text-sm leading-6 text-oxford-blue/75'>
                {t('shortBody')}{' '}
                <Link
                  href='/legal/cookie-policy'
                  className='font-medium text-oxford-blue underline underline-offset-2 hover:text-orange-peel'
                >
                  {t('cookiePolicy')}
                </Link>
              </p>

              <div className='mt-auto space-y-3'>
                <Button type='button' className={cn('w-full', primaryActionClass)} onClick={handleAllowAll}>
                  {t('allowAll')}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  className={cn('w-full', secondaryActionClass)}
                  onClick={handleDeny}
                >
                  {t('deny')}
                </Button>
                <button
                  type='button'
                  onClick={() => setView('customize')}
                  className='flex h-11 w-full items-center justify-center rounded-xl text-sm font-semibold text-oxford-blue/80 transition-colors hover:bg-seasalt-50 hover:text-oxford-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel'
                >
                  {t('customize')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className='flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6 sm:py-6'>
                <button
                  type='button'
                  onClick={() => setView('simple')}
                  className='mb-4 text-sm font-medium text-oxford-blue/65 hover:text-oxford-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel'
                >
                  {t('back')}
                </button>

                <p className='text-left text-xl font-semibold tracking-tight text-oxford-blue'>
                  {t('title')}
                </p>
                <p className='mt-2 text-sm leading-6 text-oxford-blue/75'>{t('body')}</p>

                <div className='mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <CategoryToggle
                    title={t('necessaryTitle')}
                    checked
                    disabled
                    label={t('toggleLabel', { category: t('necessaryTitle') })}
                    switchClassName={categorySwitchClass}
                  />
                  <CategoryToggle
                    title={t('preferencesTitle')}
                    checked={consent.preferences}
                    label={t('toggleLabel', { category: t('preferencesTitle') })}
                    switchClassName={categorySwitchClass}
                    onCheckedChange={checked =>
                      setConsent(prev => ({ ...prev, preferences: checked }))
                    }
                  />
                  <CategoryToggle
                    title={t('statisticsTitle')}
                    checked={consent.performance}
                    label={t('toggleLabel', { category: t('statisticsTitle') })}
                    switchClassName={categorySwitchClass}
                    onCheckedChange={checked =>
                      setConsent(prev => ({ ...prev, performance: checked }))
                    }
                  />
                  <CategoryToggle
                    title={t('marketingTitle')}
                    checked={consent.marketing}
                    label={t('toggleLabel', { category: t('marketingTitle') })}
                    switchClassName={categorySwitchClass}
                    onCheckedChange={checked =>
                      setConsent(prev => ({
                        ...prev,
                        marketing: checked,
                        adsMeasurement: checked,
                        adsPersonalization: checked ? prev.adsPersonalization : false,
                      }))
                    }
                  />
                </div>

                <Accordion type='single' collapsible className='mt-5 w-full'>
                  <DetailCategory
                    value='necessary'
                    title={t('necessaryTitle')}
                    description={t('necessaryDesc')}
                    cookies={t('necessaryCookies')}
                  />
                  <DetailCategory
                    value='preferences'
                    title={t('preferencesTitle')}
                    description={t('preferencesDesc')}
                    cookies={t('preferencesCookies')}
                  />
                  <DetailCategory
                    value='statistics'
                    title={t('statisticsTitle')}
                    description={t('statisticsDesc')}
                    cookies={t('statisticsCookies')}
                  />
                  <AccordionItem value='marketing' className='border-oxford-blue/10'>
                    <AccordionTrigger className='py-3 text-left text-sm font-semibold text-oxford-blue hover:no-underline'>
                      {t('marketingTitle')}
                    </AccordionTrigger>
                    <AccordionContent className='space-y-4 pb-4 text-sm text-oxford-blue/75'>
                      <p className='leading-relaxed'>{t('marketingDesc')}</p>
                      <p className='text-xs leading-relaxed text-oxford-blue/60'>
                        {t('marketingCookies')}
                      </p>
                      <div className='space-y-3 border-t border-oxford-blue/10 pt-3'>
                        <SubToggle
                          title={t('adsMeasurementTitle')}
                          description={t('adsMeasurementDesc')}
                          checked={consent.adsMeasurement}
                          disabled={!consent.marketing}
                          label={t('toggleLabel', { category: t('adsMeasurementTitle') })}
                          onCheckedChange={checked =>
                            setConsent(prev => ({
                              ...prev,
                              adsMeasurement: checked,
                              marketing: checked ? true : prev.marketing,
                            }))
                          }
                        />
                        <SubToggle
                          title={t('adsPersonalizationTitle')}
                          description={t('adsPersonalizationDesc')}
                          checked={consent.adsPersonalization}
                          disabled={!consent.marketing}
                          label={t('toggleLabel', { category: t('adsPersonalizationTitle') })}
                          onCheckedChange={checked =>
                            setConsent(prev => ({
                              ...prev,
                              adsPersonalization: checked,
                              marketing: checked ? true : prev.marketing,
                            }))
                          }
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <p className='mt-4 text-xs leading-5 text-oxford-blue/60'>
                  {t('aboutPolicies')}{' '}
                  <Link
                    href='/legal/cookie-policy'
                    className='font-medium text-oxford-blue underline underline-offset-2 hover:text-orange-peel'
                  >
                    {t('cookiePolicy')}
                  </Link>
                  {' · '}
                  <Link
                    href='/legal/privacy'
                    className='font-medium text-oxford-blue underline underline-offset-2 hover:text-orange-peel'
                  >
                    {t('privacyPolicy')}
                  </Link>
                </p>
              </div>

              <div className='grid grid-cols-1 gap-2 border-t border-oxford-blue/10 bg-white px-5 py-4 sm:grid-cols-3 sm:gap-3 sm:px-6'>
                <Button type='button' variant='outline' className={outlineActionClass} onClick={handleDeny}>
                  {t('deny')}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  className={outlineActionClass}
                  onClick={handleAllowSelection}
                >
                  {t('allowSelection')}
                </Button>
                <Button type='button' className={primaryActionClass} onClick={handleAllowAll}>
                  {t('allowAll')}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryToggleProps {
  title: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  switchClassName?: string;
  onCheckedChange?: (checked: boolean) => void;
}

function CategoryToggle({
  title,
  checked,
  disabled,
  label,
  switchClassName,
  onCheckedChange,
}: CategoryToggleProps) {
  return (
    <div className='flex min-h-14 items-center justify-between gap-3 rounded-xl border border-oxford-blue/12 bg-seasalt-50/60 px-3.5 py-3'>
      <p className='text-sm font-semibold text-oxford-blue'>{title}</p>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
        className={switchClassName}
      />
    </div>
  );
}

interface DetailCategoryProps {
  value: string;
  title: string;
  description: string;
  cookies: string;
}

function DetailCategory({ value, title, description, cookies }: DetailCategoryProps) {
  return (
    <AccordionItem value={value} className='border-oxford-blue/10'>
      <AccordionTrigger className='py-3 text-left text-sm font-semibold text-oxford-blue hover:no-underline'>
        {title}
      </AccordionTrigger>
      <AccordionContent className='space-y-2 pb-4 text-sm text-oxford-blue/75'>
        <p className='leading-relaxed'>{description}</p>
        <p className='text-xs leading-relaxed text-oxford-blue/60'>{cookies}</p>
      </AccordionContent>
    </AccordionItem>
  );
}

interface SubToggleProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
}

function SubToggle({
  title,
  description,
  checked,
  disabled,
  label,
  onCheckedChange,
}: SubToggleProps) {
  return (
    <div className='flex items-start justify-between gap-3'>
      <div>
        <p className='text-sm font-medium text-oxford-blue'>{title}</p>
        <p className='text-xs leading-relaxed text-oxford-blue/65'>{description}</p>
      </div>
      <Switch
        size='sm'
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
    </div>
  );
}
